import React from 'react'
import { roundWithLocale } from 'utils/math'
import Attribute, { Label, Value, AttributeList } from '../Attribute'
import Header, { Title, SubTitle } from '../Header'
import Section from '../Section'
import Separator from '../Separator'
import { LayoutData } from 'modules/shares/Inspector/types'
import { copyColumns, copyRows } from './copy'

const renderColumns = ({
  totalWidth,
  horizontalOffset,
  numberOfColumns,
  guttersOutside,
  gutterWidth,
  columnWidth,
}: LayoutData) => {
  const hasColumnInfo =
    totalWidth ||
    horizontalOffset ||
    numberOfColumns ||
    guttersOutside === false ||
    guttersOutside === true
  if (!hasColumnInfo) return null

  const sectionCopyValue = copyColumns({
    totalWidth,
    horizontalOffset,
    numberOfColumns,
    guttersOutside,
    gutterWidth,
    columnWidth,
  })

  return (
    <>
      <Separator />
      <Header copyValue={sectionCopyValue}>
        <SubTitle>Columns</SubTitle>
      </Header>
      <AttributeList>
        {totalWidth ? (
          <Attribute display="wide" copyValue={`${totalWidth}px`}>
            <Label>Total Width</Label>
            <Value>{roundWithLocale(totalWidth, 2)}px</Value>
          </Attribute>
        ) : null}

        {horizontalOffset ? (
          <Attribute display="wide" copyValue={`${horizontalOffset}px`}>
            <Label>Offset</Label>
            <Value>{roundWithLocale(horizontalOffset, 2)}px</Value>
          </Attribute>
        ) : null}

        {!isNaN(numberOfColumns as number) ? (
          <Attribute display="wide" copyValue={String(numberOfColumns)}>
            <Label>Number of Columns</Label>
            <Value>{String(numberOfColumns)}</Value>
          </Attribute>
        ) : null}

        {guttersOutside === true || guttersOutside === false ? (
          <Attribute display="wide">
            <Label>Gutter Outside</Label>
            <Value>{guttersOutside ? 'Yes' : 'No'}</Value>
          </Attribute>
        ) : null}
      </AttributeList>
      {gutterWidth || columnWidth ? (
        <>
          <Separator />
          <AttributeList>
            {gutterWidth ? (
              <Attribute display="wide" copyValue={`${gutterWidth}px`}>
                <Label>Gutter Width</Label>
                <Value>{roundWithLocale(gutterWidth, 2)}px</Value>
              </Attribute>
            ) : null}
            {columnWidth ? (
              <Attribute display="wide" copyValue={`${columnWidth}px`}>
                <Label>Column Width</Label>
                <Value>{roundWithLocale(columnWidth, 2)}px</Value>
              </Attribute>
            ) : null}
          </AttributeList>
        </>
      ) : null}
    </>
  )
}

const renderRows = ({ gutterHeight, rowHeightMultiplication }: LayoutData) => {
  const hasRows = gutterHeight || rowHeightMultiplication
  const rowHeight =
    rowHeightMultiplication && gutterHeight
      ? rowHeightMultiplication * gutterHeight
      : undefined
  if (!hasRows) return null

  const sectionCopyValue = copyRows({ gutterHeight, rowHeightMultiplication })

  return (
    <>
      <Separator />
      <Header copyValue={sectionCopyValue}>
        <SubTitle>Rows</SubTitle>
      </Header>
      <AttributeList>
        {gutterHeight ? (
          <Attribute display="wide" copyValue={`${gutterHeight}px`}>
            <Label>Gutter Height</Label>
            <Value>{roundWithLocale(gutterHeight, 2)}px</Value>
          </Attribute>
        ) : null}
        {rowHeight ? (
          <Attribute display="wide" copyValue={`${rowHeight}px`}>
            <Label>Row Height</Label>
            <Value>{roundWithLocale(rowHeight, 2)}px</Value>
          </Attribute>
        ) : null}
      </AttributeList>
    </>
  )
}

const LayoutSettings = ({ layout }: { layout?: LayoutData }) => {
  if (!layout || layout.isEnabled === false) return null
  const columns = renderColumns(layout)
  const rows = renderRows(layout)
  if (!columns && !rows) return null

  const sectionCopyValue = copyColumns(layout) + `\n\n` + copyRows(layout)

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-artboard-layout-settings">
        <Header copyValue={sectionCopyValue}>
          <Title>Layout Settings</Title>
        </Header>
        {columns}
        {rows}
      </Section>
    </>
  )
}
export default LayoutSettings
